import api from '../utils/api';

/**
 * Payment Gateway Service - Handles Razorpay, PhonePe and payment gateway operations
 */

export const paymentService = {
  /**
   * Get payment gateway configuration
   * @returns {Promise}
   */
  getGatewayConfig: async () => {
    try {
      const response = await api.get('/admin/payments/gateway-config');
      return response.data.data || response.data;
    } catch (error) {
      // Provide more detailed error information
      const errorMessage = error.response?.data?.message || 
                          (error.response?.status === 404 
                            ? 'Payment gateway routes not found. Backend server may need to be restarted.' 
                            : 'Failed to fetch gateway configuration');
      throw new Error(errorMessage);
    }
  },

  /**
   * Update payment gateway configuration
   * @param {Object} config - Gateway configuration
   * @returns {Promise}
   */
  updateGatewayConfig: async (config) => {
    try {
      const response = await api.put('/admin/payments/gateway-config', config);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update gateway configuration');
    }
  },

  /**
   * Test payment gateway connection
   * @param {string} gateway - 'razorpay' or 'phonepe'
   * @returns {Promise}
   */
  testGatewayConnection: async (gateway) => {
    try {
      const response = await api.post(`/admin/payments/test-gateway/${gateway}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to test gateway connection');
    }
  },

  /**
   * Get webhook configuration
   * @returns {Promise}
   */
  getWebhookConfig: async () => {
    try {
      const response = await api.get('/admin/payments/webhooks');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch webhook configuration');
    }
  },

  /**
   * Create/Update webhook
   * @param {Object} webhookData - Webhook configuration
   * @returns {Promise}
   */
  updateWebhook: async (webhookData) => {
    try {
      const response = await api.post('/admin/payments/webhooks', webhookData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update webhook');
    }
  },

  /**
   * Test webhook
   * @param {string} webhookId - Webhook ID
   * @returns {Promise}
   */
  testWebhook: async (webhookId) => {
    try {
      const response = await api.post(`/admin/payments/webhooks/${webhookId}/test`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to test webhook');
    }
  },

  /**
   * Get webhook logs
   * @param {Object} filters - Filters (gateway, status, startDate, endDate)
   * @returns {Promise}
   */
  getWebhookLogs: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.gateway) params.append('gateway', filters.gateway);
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await api.get(`/admin/payments/webhooks/logs?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch webhook logs');
    }
  },

  /**
   * Get reconciliation data
   * @param {Object} filters - Filters (dateRange, gateway, status)
   * @returns {Promise}
   */
  getReconciliationData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.gateway) params.append('gateway', filters.gateway);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`/admin/payments/reconciliation?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch reconciliation data');
    }
  },

  /**
   * Run reconciliation
   * @param {Object} reconciliationData - Reconciliation parameters
   * @returns {Promise}
   */
  runReconciliation: async (reconciliationData) => {
    try {
      const response = await api.post('/admin/payments/reconciliation/run', reconciliationData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to run reconciliation');
    }
  },

  /**
   * Resolve reconciliation discrepancy
   * @param {string} discrepancyId - Discrepancy ID
   * @param {Object} resolution - Resolution data
   * @returns {Promise}
   */
  resolveDiscrepancy: async (discrepancyId, resolution) => {
    try {
      const response = await api.post(`/admin/payments/reconciliation/${discrepancyId}/resolve`, resolution);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resolve discrepancy');
    }
  },

  /**
   * Get transaction details
   * @param {string} transactionId - Transaction ID
   * @returns {Promise}
   */
  getTransactionDetails: async (transactionId) => {
    try {
      const response = await api.get(`/admin/payments/transactions/${transactionId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch transaction details');
    }
  },

  /**
   * Get payment gateway transactions
   * @param {Object} filters - Filters (gateway, status, startDate, endDate)
   * @returns {Promise}
   */
  getGatewayTransactions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.gateway) params.append('gateway', filters.gateway);
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await api.get(`/admin/payments/gateway-transactions?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch gateway transactions');
    }
  },

  /**
   * Refund transaction
   * @param {string} transactionId - Transaction ID
   * @param {Object} refundData - Refund details
   * @returns {Promise}
   */
  refundTransaction: async (transactionId, refundData) => {
    try {
      const response = await api.post(`/admin/payments/transactions/${transactionId}/refund`, refundData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to process refund');
    }
  },

  /**
   * Get payment statistics
   * @param {Object} filters - Filters (dateRange, gateway)
   * @returns {Promise}
   */
  getPaymentStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.gateway) params.append('gateway', filters.gateway);
      
      const response = await api.get(`/admin/payments/stats?${params.toString()}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payment statistics');
    }
  }
};

export default paymentService;


